// src/components/notifications/NotificationCenter.tsx
import React, { useState, useMemo } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  Badge,
  ActionIcon,
  ScrollArea,
  Paper,
  Divider,
  Select,
  TextInput,
  Flex,
  Center,
  Title,
  Tooltip,
  Menu,
} from '@mantine/core';
import {
  IconBell,
  IconCheck,
  IconX,
  IconSearch,
  IconFilter,
  IconTrash,
  IconEye,
  IconEyeOff,
  IconDots,
  IconChecks,
  IconRefresh,
} from '@tabler/icons-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { 
  Notification, 
  NotificationType, 
  NotificationPriority, 
  NotificationFilter 
} from '../../types/notifications';
import { Link } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';

interface NotificationCenterProps {
  opened: boolean;
  onClose: () => void;
}

export function NotificationCenter({ opened, onClose }: NotificationCenterProps) {
  const {
    getFilteredNotifications,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    unreadCount,
    stats,
  } = useNotifications();

  const [filter, setFilter] = useState<NotificationFilter>({});
  const [searchText, setSearchText] = useState('');

  const filteredNotifications = useMemo(() => {
    return getFilteredNotifications({
      ...filter,
      searchText: searchText || undefined,
    });
  }, [filter, searchText, getFilteredNotifications]);

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  const handleClearAll = () => {
    if (window.confirm('Sei sicuro di voler eliminare tutte le notifiche?')) {
      clearAllNotifications();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group justify="space-between" style={{ width: '100%' }}>
          <Group>
            <IconBell size="1.2rem" />
            <Title order={3}>Centro Notifiche</Title>
          </Group>
          <Badge size="sm" color={unreadCount > 0 ? 'red' : 'gray'} style={{ color: 'white' }}>
            {unreadCount} non lette
          </Badge>
        </Group>
      }
      size="lg"
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Stack gap="md">
        {/* Header Actions */}
        <Group justify="space-between">
          <Group>
            <Button 
              size="xs" 
              variant="light" 
              leftSection={<IconChecks size="0.9rem" />}
              onClick={handleMarkAllAsRead}
              disabled={unreadCount === 0}
            >
              Segna tutte come lette
            </Button>
            <Button 
              size="xs" 
              variant="light" 
              color="red"
              leftSection={<IconTrash size="0.9rem" />}
              onClick={handleClearAll}
              disabled={stats.total === 0}
            >
              Elimina tutte
            </Button>
          </Group>
        </Group>

        {/* Filters */}
        <Paper p="sm" withBorder>
          <Stack gap="sm">
            <Group>
              <TextInput
                placeholder="Cerca notifiche..."
                leftSection={<IconSearch size="0.9rem" />}
                value={searchText}
                onChange={(e) => setSearchText(e.currentTarget.value)}
                style={{ flex: 1 }}
              />
              <Select
                placeholder="Tipo"
                data={[
                  { value: '', label: 'Tutti i tipi' },
                  { value: NotificationType.Assignment, label: 'Assegnazioni' },
                  { value: NotificationType.TaskCompleted, label: 'Task completati' },
                  { value: NotificationType.DeadlineAlert, label: 'Scadenze' },
                  { value: NotificationType.StatusUpdate, label: 'Aggiornamenti' },
                  { value: NotificationType.System, label: 'Sistema' },
                ]}
                value={filter.type || ''}
                onChange={(value) => setFilter(prev => ({ 
                  ...prev, 
                  type: value as NotificationType || undefined 
                }))}
                clearable
              />
              <Select
                placeholder="Stato"
                data={[
                  { value: '', label: 'Tutte' },
                  { value: 'unread', label: 'Non lette' },
                  { value: 'read', label: 'Lette' },
                ]}
                value={filter.isRead === undefined ? '' : filter.isRead ? 'read' : 'unread'}
                onChange={(value) => setFilter(prev => ({ 
                  ...prev, 
                  isRead: value === '' ? undefined : value === 'read'
                }))}
                clearable
              />
            </Group>
          </Stack>
        </Paper>

        {/* Notifications List */}
        <ScrollArea h={400}>
          <Stack gap="xs">
            {filteredNotifications.length === 0 ? (
              <Center py="xl">
                <Stack align="center" gap="sm">
                  <IconBell size="3rem" color="gray" />
                  <Text c="dimmed">
                    {stats.total === 0 
                      ? 'Nessuna notifica'
                      : 'Nessuna notifica corrisponde ai filtri'
                    }
                  </Text>
                </Stack>
              </Center>
            ) : (
              filteredNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={markAsRead}
                  onMarkAsUnread={markAsUnread}
                  onDelete={deleteNotification}
                />
              ))
            )}
          </Stack>
        </ScrollArea>

        {/* Stats Footer */}
        {stats.total > 0 && (
          <>
            <Divider />
            <Group justify="space-between" align="center">
              <Text size="sm" c="dimmed">
                Totale: {stats.total} | Non lette: {stats.unread}
              </Text>
              <Group gap="xs">
                <Badge size="sm" color="blue" style={{ color: 'white' }}>
                  Assegnazioni: {stats.byType.assignment}
                </Badge>
                <Badge size="sm" color="green" style={{ color: 'white' }}>
                  Completati: {stats.byType.task_completed}
                </Badge>
                <Badge size="sm" color="orange" style={{ color: 'white' }}>
                  Scadenze: {stats.byType.deadline_alert}
                </Badge>
              </Group>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onMarkAsUnread: (id: string) => void;
  onDelete: (id: string) => void;
}

function NotificationItem({ 
  notification, 
  onMarkAsRead, 
  onMarkAsUnread, 
  onDelete 
}: NotificationItemProps) {
  const getTypeColor = (type: NotificationType) => {
    switch (type) {
      case NotificationType.Assignment: return 'blue';
      case NotificationType.TaskCompleted: return 'green';
      case NotificationType.DeadlineAlert: return 'orange';
      case NotificationType.StatusUpdate: return 'cyan';
      case NotificationType.Error: return 'red';
      case NotificationType.Warning: return 'yellow';
      case NotificationType.Success: return 'green';
      default: return 'gray';
    }
  };

  const getPriorityColor = (priority: NotificationPriority) => {
    switch (priority) {
      case NotificationPriority.Critical: return 'red';
      case NotificationPriority.High: return 'orange';
      case NotificationPriority.Medium: return 'yellow';
      default: return 'gray';
    }
  };

  return (
    <Paper
      p="sm"
      withBorder
      style={{
        opacity: notification.isRead ? 0.7 : 1,
        borderLeftWidth: notification.isRead ? 1 : 4,
        borderLeftColor: notification.isRead ? undefined : 'var(--mantine-primary-color-filled)',
      }}
    >
      <Group justify="space-between" align="flex-start">
        <Stack gap="xs" style={{ flex: 1 }}>
          <Group justify="space-between" align="flex-start">
            <Group gap="xs">
              <Badge size="xs" color={getTypeColor(notification.type)} style={{ color: 'white' }}>
                {notification.type.replace('_', ' ')}
              </Badge>
              <Badge size="xs" color={getPriorityColor(notification.priority)} variant="light">
                {notification.priority}
              </Badge>
              {!notification.isRead && (
                <Badge size="xs" color="blue" style={{ color: 'white' }}>
                  Nuovo
                </Badge>
              )}
            </Group>
            <Text size="xs" c="dimmed">
              {formatDistanceToNow(notification.createdAt, { 
                addSuffix: true, 
                locale: it 
              })}
            </Text>
          </Group>
          
          <Text fw={500} size="sm">
            {notification.title}
          </Text>
          
          <Text size="sm" c="dimmed">
            {notification.message}
          </Text>
          
          {notification.actionUrl && (
            <Group mt="xs">
              <Button 
                size="xs" 
                variant="light"
                component={Link}
                to={notification.actionUrl}
                onClick={() => !notification.isRead && onMarkAsRead(notification.id)}
              >
                {notification.actionText || 'Visualizza'}
              </Button>
            </Group>
          )}
        </Stack>
        
        <Menu position="bottom-end">
          <Menu.Target>
            <ActionIcon variant="subtle" size="sm">
              <IconDots size="1rem" />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {notification.isRead ? (
              <Menu.Item
                leftSection={<IconEyeOff size="1rem" />}
                onClick={() => onMarkAsUnread(notification.id)}
              >
                Segna come non letta
              </Menu.Item>
            ) : (
              <Menu.Item
                leftSection={<IconEye size="1rem" />}
                onClick={() => onMarkAsRead(notification.id)}
              >
                Segna come letta
              </Menu.Item>
            )}
            <Menu.Divider />
            <Menu.Item
              leftSection={<IconTrash size="1rem" />}
              color="red"
              onClick={() => onDelete(notification.id)}
            >
              Elimina
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Paper>
  );
}